import { useEffect, useState, useMemo, useRef } from "react";
import { PageSection, ReportTable, Pagination, StatusBadge } from "../components/reporting";
import type { Column } from "../components/reporting";
import { LeadDrawer } from "../components/LeadDrawer";
import { deliveryStateMeta, outcomeCatalog } from "../data/leads";
import type { Lead, LeadStatus, DeliveryState, Outcome } from "../data/leads";
import { useScreenInit } from "../useScreenInit";
import { useSession } from "../session";
import { ApiError, api, apiBlob, eraseLead, getLeads } from "../api";
import { tenantFromLeadSearch } from "../lead-links";
import type { LeadSummary, UpdateLead } from "@fmc/contracts";

type StatusFilter = "all" | LeadStatus;
type FwdFilter = "all" | DeliveryState;
type ExportFormat = "csv" | "xlsx";

const columns: Column[] = [
  { label: "Lead", align: "left" },
  { label: "Email address", align: "left", width: "280px" },
  { label: "Status", align: "left", width: "88px" },
  { label: "Received", align: "left", width: "110px" },
  { label: "Delivery", align: "left", width: "210px" },
];

function StatChip({
  value,
  label,
  accent,
}: {
  value: string;
  label: string;
  accent?: "green" | "amber" | "red";
}) {
  const dot =
    accent === "green"
      ? "bg-status-green"
      : accent === "amber"
        ? "bg-status-amber"
        : accent === "red"
          ? "bg-status-red"
          : "bg-tertiary";
  return (
    <div className="flex items-center gap-2.5">
      <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
      <span className="text-title-md font-bold text-primary leading-none">{value}</span>
      <span className="text-caption text-tertiary">{label}</span>
    </div>
  );
}

export function Leads() {
  const seed = useScreenInit();
  const session = useSession();
  const { canEditLeads, role } = session;
  const [leadList, setLeadList] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loadError, setLoadError] = useState("");
  const [status, setStatus] = useState<StatusFilter>((seed.status as StatusFilter) ?? "all");
  const [fwd, setFwd] = useState<FwdFilter>((seed.fwd as FwdFilter) ?? "all");
  const [query, setQuery] = useState((seed.query as string) ?? "");
  const [selected, setSelected] = useState<Lead | null>(null);
  const [outcomesByLead, setOutcomesByLead] = useState<Record<string, Outcome[]>>({});
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [exporting, setExporting] = useState<ExportFormat | null>(null);
  const exportMenuRef = useRef<HTMLDivElement>(null);
  const availableLeads = seed.empty ? [] : leadList;

  useEffect(() => {
    if (!exportMenuOpen) return;

    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!exportMenuRef.current?.contains(event.target as Node)) setExportMenuOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setExportMenuOpen(false);
    };

    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [exportMenuOpen]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void (async () => {
      const targetTenant = tenantFromLeadSearch(window.location.search);
      if (targetTenant && targetTenant !== session.tenantKey) {
        const entered = await session.enterAdminTenant(targetTenant);
        if (!entered) {
          const parameters = new URLSearchParams(window.location.search);
          parameters.delete("tenant");
          window.history.replaceState(
            window.history.state,
            "",
            `${window.location.pathname}?${parameters.toString()}`,
          );
        }
      }
      if (cancelled) return;
      const { items, nextCursor: cursor } = await getLeads();
      if (!cancelled) {
        setLeadList(items.map(toUiLead));
        setNextCursor(cursor);
      }
    })()
      .catch((error: Error) => {
        if (!cancelled) setLoadError(error.message);
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const addOutcome = async (leadId: string, outcome: Outcome) => {
    await api(`/api/leads/${encodeURIComponent(leadId)}/outcomes`, {
      method: "POST",
      body: JSON.stringify({
        type: outcome.type,
        valuePence: outcome.value ? Math.round(outcome.value * 100) : null,
        note: outcome.note,
      }),
    });
    setOutcomesByLead((prev) => ({ ...prev, [leadId]: [...(prev[leadId] ?? []), outcome] }));
    if (outcome.type === "won" || outcome.type === "lost") {
      setLeadList((current) =>
        current.map((lead) =>
          lead.id === leadId ? { ...lead, finalOutcome: outcome.type as "won" | "lost" } : lead,
        ),
      );
      setSelected((current) =>
        current?.id === leadId
          ? { ...current, finalOutcome: outcome.type as "won" | "lost" }
          : current,
      );
    }
  };

  const openLead = async (
    target: Lead | string,
    allowLegacyAdminResolution = true,
  ): Promise<void> => {
    const leadId = typeof target === "string" ? target : target.id;
    if (typeof target !== "string") setSelected(target);
    setLoadError("");
    try {
      const detail = await api<
        LeadSummary & {
          notes: Array<{ body: string; createdAt: string; createdBy: string }>;
          outcomes: Array<{
            id: string;
            type: Outcome["type"];
            valuePence: number | null;
            note: string | null;
            createdAt: string;
            createdBy: string;
          }>;
          deliveryAttempts: Array<{
            channel: "sales_email" | "webhook";
            destination: string;
            status: string;
            responseCode: number | null;
            attemptedAt: string;
            deliveryKind: "initial" | "reminder_1" | "reminder_2";
          }>;
        }
      >(`/api/leads/${encodeURIComponent(leadId)}`);
      const outcomes = detail.outcomes.map((outcome) => ({
        id: outcome.id,
        type: outcome.type,
        ...(outcome.valuePence === null ? {} : { value: outcome.valuePence / 100 }),
        ...(outcome.note ? { note: outcome.note } : {}),
        at: new Date(outcome.createdAt).toLocaleDateString("en-GB"),
        by: outcome.createdBy,
      }));
      setLeadList((current) =>
        current.map((lead) => (lead.id === leadId ? { ...lead, isNew: detail.isNew } : lead)),
      );
      setOutcomesByLead((current) => ({ ...current, [leadId]: outcomes }));
      setSelected({
        ...toUiLead(detail),
        notes: detail.notes
          .map(
            (note) =>
              `${note.body}\n— ${note.createdBy}, ${new Date(note.createdAt).toLocaleString("en-GB")}`,
          )
          .join("\n\n"),
        deliveryLog: detail.deliveryAttempts.map((attempt) => ({
          channel:
            attempt.channel === "sales_email" ? ("Sales email" as const) : ("Webhook" as const),
          at: new Date(attempt.attemptedAt).toLocaleString("en-GB"),
          result: ["sent", "delivered"].includes(attempt.status)
            ? ("success" as const)
            : attempt.status === "retry"
              ? ("retry" as const)
              : ("failed" as const),
          detail: `${attempt.destination} · ${attempt.status}${attempt.responseCode ? ` · HTTP ${attempt.responseCode}` : ""}`,
          deliveryKind: attempt.deliveryKind,
        })),
      });
    } catch (problem) {
      const linkParameters = new URLSearchParams(window.location.search);
      if (
        allowLegacyAdminResolution &&
        problem instanceof ApiError &&
        problem.status === 404 &&
        session.isFmcAdmin &&
        !linkParameters.has("tenant")
      ) {
        try {
          const resolved = await api<{ tenantId: string }>(
            `/api/admin/leads/${encodeURIComponent(leadId)}/tenant`,
          );
          if (await session.enterAdminTenant(resolved.tenantId)) {
            linkParameters.set("tenant", resolved.tenantId);
            window.location.replace(`${window.location.pathname}?${linkParameters.toString()}`);
            return;
          }
        } catch {
          // Do not reveal whether a lead or tenant exists to an unauthorised session.
        }
      }
      setLoadError(problem instanceof Error ? problem.message : "Lead details could not be loaded");
    }
  };

  useEffect(() => {
    if (loading || selected) return;
    const leadId = new URLSearchParams(window.location.search).get("lead");
    if (!leadId) return;
    const loadedLead = leadList.find((lead) => lead.id === leadId);
    void openLead(loadedLead ?? leadId);
  }, [loading]);

  const exportRows = async (format: ExportFormat) => {
    setExportMenuOpen(false);
    setExporting(format);
    setLoadError("");
    const blob = await apiBlob("/api/leads/export", {
      method: "POST",
      body: JSON.stringify({
        ...(status === "all" ? {} : { status }),
        ...(fwd === "all" ? {} : { delivery: fwd }),
        format,
      }),
    });
    const href = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = href;
    link.download = `fmc-leads-${new Date().toISOString().slice(0, 10)}.${format}`;
    link.click();
    URL.revokeObjectURL(href);
    setExporting(null);
  };

  const startExport = (format: ExportFormat) => {
    void exportRows(format).catch((problem) => {
      setExporting(null);
      setLoadError(problem instanceof Error ? problem.message : "Export failed");
    });
  };

  const loadMore = async () => {
    if (!nextCursor) return;
    const response = await getLeads(`?cursor=${encodeURIComponent(nextCursor)}`);
    setLeadList((current) => [...current, ...response.items.map(toUiLead)]);
    setNextCursor(response.nextCursor);
  };

  const filtered = useMemo(
    () =>
      availableLeads.filter((l) => {
        const q = query.trim().toLowerCase();
        const matchesQuery =
          !q ||
          l.name.toLowerCase().includes(q) ||
          l.email.toLowerCase().includes(q) ||
          l.phone.toLowerCase().includes(q);
        return (
          matchesQuery &&
          (status === "all" || l.status === status) &&
          (fwd === "all" || l.delivery === fwd)
        );
      }),
    [availableLeads, status, fwd, query],
  );

  const counts = useMemo(
    () => ({
      total: availableLeads.length,
      mql: availableLeads.filter((l) => l.status === "MQL").length,
      sql: availableLeads.filter((l) => l.status === "SQL").length,
      failed: availableLeads.filter((l) => l.delivery === "failed").length,
    }),
    [availableLeads],
  );

  const rows = filtered.map((l) => {
    const d = deliveryStateMeta[l.delivery];
    const finalOutcome = l.finalOutcome ? outcomeCatalog[l.finalOutcome] : null;
    return [
      <button onClick={() => void openLead(l)} className="flex items-center gap-3 text-left group">
        <span className="w-9 h-9 shrink-0 rounded-full bg-surface-info text-brand-dark-teal flex items-center justify-center text-micro font-semibold">
          {l.name
            .split(" ")
            .slice(-2)
            .map((n) => n[0])
            .join("")}
        </span>
        <span className="min-w-0">
          <span className="flex items-center gap-2 min-w-0">
            <span
              className={`block text-body text-primary group-hover:text-brand-red transition-colors truncate ${l.isNew ? "font-bold" : "font-medium"}`}
            >
              {l.name}
            </span>
            {l.finalOutcome && finalOutcome && (
              <span
                aria-label={`${finalOutcome.label} outcome`}
                title={`${finalOutcome.label} outcome`}
                className={`w-6 h-6 rounded-full inline-flex items-center justify-center shrink-0 ${l.finalOutcome === "won" ? "text-status-green bg-[rgba(86,205,174,0.12)]" : "text-status-red bg-[rgba(255,101,80,0.12)]"}`}
              >
                <i className={`fa-solid ${finalOutcome.icon} text-[11px]`} />
              </span>
            )}
          </span>
          <span className="block text-micro text-tertiary truncate">
            {l.phone || "No phone supplied"}
          </span>
        </span>
        {l.isNew && <StatusBadge label="New" tone="blue" ariaLabel="New lead" />}
      </button>,
      <span
        className={`text-body truncate block ${l.isNew ? "font-bold text-primary" : "text-secondary"}`}
      >
        {l.email}
      </span>,
      <StatusBadge label={l.status} tone={l.status === "SQL" ? "green" : "blue"} />,
      <span className={`text-body ${l.isNew ? "font-bold text-primary" : "text-secondary"}`}>
        {l.capturedAt}
      </span>,
      <span className="flex items-center gap-1.5 flex-wrap">
        {l.deliveryChannels.length ? (
          l.deliveryChannels.map((channel) => {
            const channelState = deliveryStateMeta[channel.status];
            return (
              <StatusBadge
                key={channel.channel}
                label={`${channel.channel === "email" ? "Email" : "Webhook"} · ${channelState.label}`}
                tone={channelState.tone}
              />
            );
          })
        ) : (
          <StatusBadge label={d.label} tone={d.tone} />
        )}
      </span>,
    ];
  });

  const statusFilters: { key: StatusFilter; label: string }[] = [
    { key: "all", label: "All" },
    { key: "MQL", label: "MQL" },
    { key: "SQL", label: "SQL" },
  ];

  return (
    <div className="flex flex-col gap-[24px]">
      {/* Intake summary */}
      <div className="bg-surface-white border border-default rounded-xl px-[30px] py-[22px]">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <i className="fa-solid fa-bolt text-[13px] text-brand-red" />
            <span className="text-caption font-semibold uppercase tracking-[0.06em] text-tertiary">
              Inbound this period
            </span>
          </div>
          <span className="inline-flex items-center gap-2 text-caption text-tertiary">
            <span className="w-1.5 h-1.5 rounded-full bg-status-green" />
            {availableLeads[0]
              ? `Latest loaded lead · ${availableLeads[0].capturedAt}`
              : "Webhook route provisioned"}
          </span>
        </div>
        <div className="flex items-center gap-8 flex-wrap">
          <StatChip value={String(counts.total)} label="Leads received" />
          <span className="w-px h-6 bg-default" />
          <StatChip value={String(counts.mql)} label="MQLs" accent="amber" />
          <StatChip value={String(counts.sql)} label="SQLs" accent="green" />
          <span className="w-px h-6 bg-default" />
          <StatChip value={String(counts.failed)} label="Failed to forward" accent="red" />
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="relative">
          <i className="fa-solid fa-magnifying-glass text-[13px] text-tertiary absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name, email or phone"
            className="h-9 w-[300px] pl-9 pr-3 rounded-xs border border-default bg-surface-white text-body text-primary placeholder:text-tertiary focus:outline-none focus:border-brand-red/50 transition-colors"
          />
        </div>

        <div className="flex items-center gap-3">
          <div className="flex items-center rounded-xs border border-default overflow-hidden">
            {statusFilters.map((f) => (
              <button
                key={f.key}
                onClick={() => setStatus(f.key)}
                className={`px-3 h-9 text-caption font-medium transition-colors ${
                  status === f.key
                    ? "bg-brand-red-tint text-brand-red"
                    : "bg-surface-white text-secondary hover:bg-surface-light"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <select
            value={fwd}
            onChange={(e) => setFwd(e.target.value as FwdFilter)}
            className="h-9 px-3 rounded-xs border border-default bg-surface-white text-caption font-medium text-secondary cursor-pointer hover:bg-surface-light transition-colors"
          >
            <option value="all">All forwarding</option>
            <option value="delivered">Forwarded</option>
            <option value="pending">Pending</option>
            <option value="not_requested">Destination not configured</option>
            <option value="failed">Failed</option>
          </select>
          <div ref={exportMenuRef} className="relative">
            <button
              type="button"
              onClick={() => setExportMenuOpen((open) => !open)}
              disabled={!role || !["owner", "manager"].includes(role) || exporting !== null}
              title={
                !role || !["owner", "manager"].includes(role)
                  ? "Owner or manager access required"
                  : undefined
              }
              aria-haspopup="menu"
              aria-expanded={exportMenuOpen}
              className="h-9 px-3.5 rounded-xs border border-default bg-surface-white flex items-center gap-2.5 text-body font-medium text-button hover:bg-surface-light transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <i className="fa-solid fa-download text-[13px]" aria-hidden="true" />
              {exporting ? "Preparing…" : "Export"}
              <i
                className={`fa-solid fa-chevron-down text-[10px] text-tertiary transition-transform ${exportMenuOpen ? "rotate-180" : ""}`}
                aria-hidden="true"
              />
            </button>

            {exportMenuOpen && (
              <div
                role="menu"
                aria-label="Export format"
                className="absolute right-0 top-[calc(100%+8px)] z-30 w-[250px] overflow-hidden rounded-md border border-default bg-surface-white p-1.5 shadow-[0_12px_32px_rgba(38,56,74,0.16)]"
              >
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => startExport("csv")}
                  className="flex w-full items-center gap-3 rounded-xs px-3 py-2.5 text-left hover:bg-surface-light transition-colors"
                >
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xs bg-surface-info text-brand-dark-teal">
                    <i className="fa-solid fa-file-csv text-[15px]" aria-hidden="true" />
                  </span>
                  <span>
                    <span className="block text-body font-semibold text-primary">CSV file</span>
                    <span className="block text-caption text-tertiary">
                      For imports and data tools
                    </span>
                  </span>
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => startExport("xlsx")}
                  className="flex w-full items-center gap-3 rounded-xs px-3 py-2.5 text-left hover:bg-surface-light transition-colors"
                >
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xs bg-[rgba(41,166,118,0.1)] text-status-green">
                    <i className="fa-solid fa-file-excel text-[15px]" aria-hidden="true" />
                  </span>
                  <span>
                    <span className="block text-body font-semibold text-primary">
                      Excel workbook
                    </span>
                    <span className="block text-caption text-tertiary">Formatted .xlsx file</span>
                  </span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <PageSection
        title={`${filtered.length} loaded leads`}
        right={
          <span className="text-caption text-tertiary">
            Captured via HubSpot webhook · forwarded to connected destinations
          </span>
        }
        bodyClassName="px-[20px] pt-[16px] pb-[20px]"
      >
        {loading ? (
          <div className="py-14 text-center text-body text-secondary">
            Loading tenant-scoped leads…
          </div>
        ) : loadError ? (
          <div className="py-14 text-center text-body text-status-red">{loadError}</div>
        ) : rows.length > 0 ? (
          <>
            <ReportTable
              columns={columns}
              rows={rows}
              rowClassName={(rowIndex) =>
                filtered[rowIndex]?.isNew ? "bg-[#f3f4f6] hover:bg-[#eceff1]" : ""
              }
            />
            <Pagination from={1} to={filtered.length} total={filtered.length} />
            {nextCursor && (
              <div className="flex justify-center pt-4">
                <button
                  onClick={() =>
                    void loadMore().catch((problem) =>
                      setLoadError(
                        problem instanceof Error
                          ? problem.message
                          : "More leads could not be loaded",
                      ),
                    )
                  }
                  className="h-9 px-4 rounded-xs border border-default bg-surface-white text-body font-semibold text-button"
                >
                  Load more leads
                </button>
              </div>
            )}
          </>
        ) : availableLeads.length === 0 ? (
          <div className="py-14 flex flex-col items-center text-center">
            <span className="w-12 h-12 rounded-full bg-surface-info text-brand-dark-teal flex items-center justify-center mb-4">
              <i className="fa-solid fa-inbox text-[22px]" />
            </span>
            <p className="text-heading font-semibold text-primary">
              Your endpoint is live — leads will appear here
            </p>
            <p className="text-body text-secondary max-w-[520px] mt-2 leading-relaxed">
              Leads captured by FMC arrive here automatically within seconds of a form submission.
              Nothing needs to be set up to start receiving them.
            </p>
            <div className="flex items-center gap-3 mt-5">
              <a
                href="/delivery"
                className="h-9 px-3.5 rounded-xs border border-default bg-surface-white text-body font-medium text-secondary flex items-center hover:bg-surface-light transition-colors"
              >
                View your endpoint
              </a>
              <a
                href="/delivery"
                className="h-9 px-3.5 rounded-xs border border-default bg-surface-white text-body font-medium text-secondary flex items-center hover:bg-surface-light transition-colors"
              >
                Set up a delivery destination
              </a>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 mt-6">
              {[
                "Endpoint live and listening",
                "Delivery destinations optional",
                "Verified destinations receive accepted leads",
              ].map((message) => (
                <span
                  key={message}
                  className="inline-flex items-center gap-1.5 text-micro text-secondary"
                >
                  <i className="fa-solid fa-circle-check text-status-green text-[11px]" />
                  {message}
                </span>
              ))}
            </div>
          </div>
        ) : (
          <div className="py-16 text-center">
            <i className="fa-solid fa-inbox text-[26px] text-tertiary mb-3" />
            <p className="text-body text-secondary">No leads match these filters.</p>
          </div>
        )}
      </PageSection>

      <LeadDrawer
        lead={selected}
        outcomes={selected ? (outcomesByLead[selected.id] ?? []) : []}
        onAddOutcome={addOutcome}
        onClose={() => setSelected(null)}
        onDelete={async (leadId, password) => {
          if (!canEditLeads) return;
          await eraseLead(leadId, password);
          setLeadList((current) => current.filter((lead) => lead.id !== leadId));
          setSelected(null);
        }}
        onAddNote={async (leadId, body) => {
          await api(`/api/leads/${encodeURIComponent(leadId)}/notes`, {
            method: "POST",
            body: JSON.stringify({ body }),
          });
          if (selected?.id === leadId) {
            setSelected({
              ...selected,
              notes: [selected.notes, `${body}\n— You, ${new Date().toLocaleString("en-GB")}`]
                .filter(Boolean)
                .join("\n\n"),
            });
          }
        }}
        onUpdate={async (leadId: string, values: UpdateLead) => {
          const updated = await api<LeadSummary>(`/api/leads/${encodeURIComponent(leadId)}`, {
            method: "PATCH",
            body: JSON.stringify(values),
          });
          const next = toUiLead(updated);
          setLeadList((current) => current.map((lead) => (lead.id === leadId ? next : lead)));
          setSelected((current) =>
            current?.id === leadId
              ? {
                  ...current,
                  ...next,
                  deliveryLog: current.deliveryLog,
                  notes: current.notes,
                  outcomes: current.outcomes,
                }
              : current,
          );
        }}
      />
    </div>
  );
}

function toUiLead(lead: LeadSummary): Lead {
  return {
    id: lead.id,
    version: lead.version,
    isNew: lead.isNew,
    firstName: lead.firstName ?? "",
    lastName: lead.lastName ?? "",
    name: lead.name,
    email: lead.email,
    phone: lead.phone,
    jobTitle: lead.jobTitle,
    practice: lead.practice,
    practicePostcode: lead.practicePostcode,
    practiceAddress: lead.practiceAddress,
    practiceType: lead.practiceType,
    enquiryNotes: lead.enquiryNotes,
    source: lead.source,
    status: lead.status,
    delivery: lead.delivery === "paused" ? "failed" : lead.delivery,
    deliveryChannels: (lead.deliveryChannels ?? []).map((channel) => ({
      channel: channel.channel,
      status: channel.status,
    })),
    deliveryLog: [],
    notes: "",
    capturedAt: new Date(lead.capturedAt).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }),
    finalOutcome: lead.finalOutcome ?? null,
    outcomes: [],
  };
}
